/**
 * Data for the Site Profile — the item-level view that mirrors the FCCERS-3
 * Profile sheet (manual p. 97).
 *
 * The rest of the dashboard stops at subscale level, which is where the
 * numbers stop being actionable: "Activities 2.80" does not tell a provider
 * what to change, "Dramatic play 2" does. This assembles the per-item scores
 * so two or more visits to the same site can be laid side by side.
 *
 * Per-item scores live in the Sheet's column-W JSON blob, so observations
 * submitted before that column existed simply have no detail and are excluded
 * rather than drawn as gaps.
 */

import type { DashboardObservation, SubscaleAverages } from './types';
import { SUBSCALE_META } from './types';

export interface ProfileItem {
  id: string;
  number: number;
  name: string;
  subscaleId: string;
}

export interface ProfileObservation {
  recordId: string;
  date: string;
  observerName: string;
  totalAverage: number | null;
  subscaleAverages: SubscaleAverages;
  /** item number -> score; NA and unscored items are absent. */
  scores: Map<number, number>;
  /** item numbers explicitly marked Not Applicable. */
  naItems: Set<number>;
}

export interface SiteProfile {
  siteName: string;
  /** Newest first — the order the picker offers them in. */
  observations: ProfileObservation[];
  /** Every item seen across those observations, in rubric order. */
  items: ProfileItem[];
}

export function hasItemDetail(o: DashboardObservation): boolean {
  return Boolean(o.detail?.subscales?.some((s) => s.items?.length));
}

/** Site name as the dashboard keys it, falling back to the provider. */
function siteKey(o: DashboardObservation): string {
  return (o.siteName || o.providerName || 'Unnamed site').trim();
}

function toProfileObservation(o: DashboardObservation): ProfileObservation {
  const scores = new Map<number, number>();
  const naItems = new Set<number>();
  for (const sub of o.detail?.subscales ?? []) {
    for (const it of sub.items ?? []) {
      if (it.itemNa) naItems.add(it.number);
      else if (typeof it.score === 'number') scores.set(it.number, it.score);
    }
  }
  return {
    recordId: o.recordId,
    date: o.observationDate,
    observerName: o.observerName,
    totalAverage: o.totalAverage,
    subscaleAverages: o.subscaleAverages,
    scores,
    naItems,
  };
}

/**
 * Item list assembled from the observations themselves rather than the rubric
 * schema, so a site observed under an older item set still lines up.
 */
function collectItems(obs: DashboardObservation[]): ProfileItem[] {
  const seen = new Map<number, ProfileItem>();
  for (const o of obs) {
    for (const sub of o.detail?.subscales ?? []) {
      for (const it of sub.items ?? []) {
        if (!seen.has(it.number)) {
          seen.set(it.number, {
            id: it.id,
            number: it.number,
            name: it.name,
            subscaleId: sub.id,
          });
        }
      }
    }
  }
  return [...seen.values()].sort((a, b) => a.number - b.number);
}

/** Sites that have at least one observation carrying item-level detail. */
export function buildSiteProfiles(
  observations: DashboardObservation[],
): SiteProfile[] {
  const bySite = new Map<string, DashboardObservation[]>();
  for (const o of observations) {
    if (!hasItemDetail(o)) continue;
    const k = siteKey(o);
    bySite.set(k, [...(bySite.get(k) ?? []), o]);
  }
  return [...bySite.entries()]
    .map(([siteName, obs]) => {
      const sorted = [...obs].sort((a, b) =>
        (b.observationDate || '').localeCompare(a.observationDate || ''),
      );
      return {
        siteName,
        observations: sorted.map(toProfileObservation),
        items: collectItems(sorted),
      };
    })
    .sort(
      (a, b) =>
        b.observations.length - a.observations.length ||
        a.siteName.localeCompare(b.siteName),
    );
}

/** Subscale a given item number belongs to, for the grouping bands. */
export function subscaleOf(items: ProfileItem[], number: number) {
  const it = items.find((i) => i.number === number);
  return SUBSCALE_META.find((s) => s.id === it?.subscaleId);
}

/**
 * Item numbers whose score moved between the two observations, largest change
 * first — the "what actually shifted" list a coach wants after a revisit.
 */
export function itemDeltas(a: ProfileObservation, b: ProfileObservation) {
  const out: Array<{ number: number; from: number; to: number; delta: number }> = [];
  for (const [num, from] of a.scores) {
    const to = b.scores.get(num);
    if (typeof to === 'number' && to !== from) {
      out.push({ number: num, from, to, delta: to - from });
    }
  }
  return out.sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta));
}
